import { useQuery } from '@tanstack/react-query';
import {
  getDoctorVerificationList,
  getFormattedDoctorsList
} from '../../services/doctor-verification';
import { Doctor } from '../../pages/DoctorsVerification/types';
import { useS3Uploader, useTableData } from '../../custom-hooks';

export const useDoctorVerificationList = ({
  role,
  status,
  page = 0,
  limit = 10
}: {
  role: string;
  status?: string;
  page?: number;
  limit?: number;
}) => {
  const { setPageTimestamps, setTotalCount, setTableRows, pageTimestamps } = useTableData();
  const { generateSignedUrl } = useS3Uploader();

  return useQuery({
    queryKey: ['doctor-verification-list', role, status, page, limit],
    enabled: Boolean(role),
    staleTime: 0, //  force refetch on tab change

    queryFn: async () => {
      const updatedAt = page === 0 ? undefined : pageTimestamps.get(page - 1);

      // 1 Fetch API data
      const response = await getDoctorVerificationList(role, status ?? '', limit, updatedAt);

      // 2 Format doctors
      const token = localStorage.getItem('user_access_token') || '';
      const formattedDoctors = await getFormattedDoctorsList(status, response, token);

      // 3 Map doctors → table rows
      const rowsData = await Promise.all(
        (formattedDoctors ?? []).map(async (doctor: Doctor) => ({
          ...doctor,
          practiseAdminId: doctor.practiseAdminId ?? '',
          fullName: `${doctor.firstName ?? ''} ${doctor.lastName ?? ''}`.trim(),
          email: doctor.email ?? '',
          phoneNumber: doctor.phoneNumber ?? '',
          gender: doctor.gender ?? '',
          verificationStatus: doctor.verificationStatus ?? '',
          created_at: doctor.created_at,
          updated_at: doctor.updated_at,
          image: doctor.practiseAdminLivePic
            ? await generateSignedUrl(doctor.practiseAdminLivePic.imageContent as string)
            : ''
        }))
      );

      // 4 Store table state
      setTableRows(rowsData);
      setTotalCount(response?.total?.aggregate?.count ?? 0);

      // 5 Pagination timestamp
      if (formattedDoctors.length) {
        const lastDoctor = formattedDoctors.at(-1);

        setPageTimestamps((prev) => {
          const map = new Map(prev);
          map.set(
            page,
            lastDoctor?.updated_at
              ? new Date(lastDoctor.updated_at).toISOString()
              : new Date().toISOString()
          );
          return map;
        });
      }

      // 6 Return query data
      return {
        rows: rowsData,
        total: response?.total?.aggregate?.count ?? 0
      };
    }
  });
};
